import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  HStack,
  Link,
  useColorModeValue,
  Container,
  Button,
  Link as ChakraLink,
  Text,
  Avatar,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Divider,
  Icon,
  Input,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { SettingsIcon } from "@chakra-ui/icons";
import { FiLogOut } from "react-icons/fi";
import { supabase } from "../../lib/supabaseClient";

const CollaborationLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, logout, searchUserByEmail, restoreAdminUser } =
    useAuth();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBgColor = useColorModeValue("gray.100", "gray.700");
  const [isPartner, setIsPartner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isContractData, setIsContractData] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user?.email) return;
      const { data, error } = await supabase
        .from("user")
        .select("role, admin")
        .eq("email", user.email)
        .single();
      if (
        data?.role &&
        typeof data.role === "string" &&
        data.role.includes("Partner")
      ) {
        setIsPartner(true);
      } else {
        setIsPartner(false);
      }

      // admin 값 체크
      const isAdminUser = data?.admin?.toLowerCase() === "true";

      // role 체크
      const isAdminRole =
        data?.role &&
        (data.role.includes("Manager") || data.role.includes("All Roles"));

      setIsAdmin(isAdminUser || isAdminRole);

      const { data: contractData, error: contractError } = await supabase
        .from("shorts_contract")
        .select("created_date")
        .eq("contractor", user.email)
        .maybeSingle();

      setIsContractData(!!contractData && !contractError);
    };
    fetchUserRole();
    // localStorage에서 검색 이메일 불러오기
    const savedEmail = localStorage.getItem("searchEmail");
    if (savedEmail) setSearchEmail(savedEmail);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const mainNavItems = [
    { name: "홈", path: "/" },
    {
      name: "음반 발매",
      path: "/release",
    },
    { name: "리포트", path: "/report" },
    { name: "음원 협업", path: "/laeeblyforshorts" },
  ];

  const collaborationNavItems = [
    { name: "홈", path: "/laeeblyforshorts" },
    { name: "대시보드", path: "/collaboration/dashboard" },
    { name: "채널 관리", path: "/collaboration/channels" },
    { name: "음반 리스트", path: "/collaboration/albums" },
    { name: "수익 (영상별)", path: "/collaboration/revenue-by-video" },
    { name: "수익 (채널별)", path: "/collaboration/revenue-by-channel" },
    { name: "정산 관리", path: "/collaboration/settlements" },
  ];

  if (isPartner) {
    collaborationNavItems.push({
      name: "파트너스",
      path: "/collaboration/partners",
    });
  }

  const handleNavClick = (path) => {
    if (!user) {
      //navigate(`/login?redirect=${encodeURIComponent(path)}`);
      navigate("/login");
    } else {
      navigate(path);
    }
  };

  const handleSearchUser = async () => {
    if (!searchEmail) {
      toast({
        title: "이메일을 입력해주세요",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    // localStorage에 저장
    localStorage.setItem("searchEmail", searchEmail);
    setIsSearching(true);
    try {
      await searchUserByEmail(searchEmail);
      toast({
        title: "사용자 정보를 불러왔습니다",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "사용자를 찾을 수 없습니다",
        description: error.message,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      {/* 메인 상단바 */}
      <Box
        as="header"
        bg={bgColor}
        borderBottom="1px"
        borderColor={borderColor}
        top={0}
        zIndex={1000}
      >
        <style>{`
          .no-outline:focus, .no-outline:active {
            outline: none !important;
            box-shadow: none !important;
            border: none !important;
          }
          .chakra-popover__content:focus {
            outline: none !important;
            box-shadow: none !important;
            border: none !important;
          }
        `}</style>
        <Container maxW="container.xl">
          <Flex
            minH={{ base: "auto", md: "64px" }}
            py={{ base: 2, md: 0 }}
            alignItems="center"
            justify="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <RouterLink
                to="/"
                style={{ display: "flex", alignItems: "center" }}
              >
                <img
                  src="/images/plpl.png"
                  alt="플플 로고"
                  style={{
                    height: 42,
                    width: 42,
                    objectFit: "contain",
                    display: "block",
                    outline: "none",
                    boxShadow: "none",
                    border: "none",
                  }}
                  draggable={false}
                  tabIndex={-1}
                />
              </RouterLink>
            </Box>
            {/* 어드민용 사용자 검색 */}
            {isAdmin && (
              <Box display={{ base: "none", md: "block" }}>
                <HStack spacing={2} ml={4} flexWrap="wrap">
                  <Input
                    placeholder="보고싶은 사용자의 이메일을 입력해주세요"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    size="sm"
                    width={{ base: "200px", md: "300px" }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSearchUser();
                      }
                    }}
                  />
                  <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={handleSearchUser}
                    isLoading={isSearching}
                  >
                    등록
                  </Button>
                </HStack>
              </Box>
            )}
            {localStorage.getItem("adminUser") && (
              <Button colorScheme="gray" size="sm" onClick={restoreAdminUser}>
                돌아가기
              </Button>
            )}
            <HStack spacing={4} flex={1} justify="flex-end" mr={6}>
              {mainNavItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  onClick={() => handleNavClick(item.path)}
                  fontWeight={item.name === "음원 협업" ? "bold" : "normal"}
                  color={"black"}
                  className="no-outline"
                  size={{ base: "sm", md: "md" }}
                  display={item.display}
                  _focus={{
                    outline: "none",
                    boxShadow: "none",
                    border: "none",
                  }}
                  _active={{
                    outline: "none",
                    boxShadow: "none",
                    border: "none",
                  }}
                >
                  {item.name}
                </Button>
              ))}
            </HStack>
            <Box ml="auto">
              {user ? (
                <Popover placement="bottom-end">
                  <PopoverTrigger>
                    <HStack
                      spacing={3}
                      cursor="pointer"
                      p={2}
                      borderRadius="md"
                      _hover={{ bg: hoverBgColor }}
                      flexWrap={{ base: "wrap", md: "nowrap" }}
                      justify={{ base: "flex-end", md: "flex-start" }}
                    >
                      <Avatar
                        src={userProfile?.img_profile}
                        name={userProfile?.nickname || user.email}
                        bg="blue.400"
                        color="white"
                        size="sm"
                        className="no-outline"
                        _focus={{
                          outline: "none",
                          boxShadow: "none",
                          border: "none",
                        }}
                        _active={{
                          outline: "none",
                          boxShadow: "none",
                          border: "none",
                        }}
                      />
                      <Text fontWeight="medium">
                        {userProfile?.nickname || user.email.split("@")[0]}
                      </Text>
                    </HStack>
                  </PopoverTrigger>
                  <PopoverContent
                    w="270px"
                    p={0}
                    borderRadius="lg"
                    boxShadow="lg"
                    tabIndex={-1}
                    className="no-outline"
                    _focus={{
                      outline: "none !important",
                      boxShadow: "none !important",
                      border: "none !important",
                    }}
                    _active={{
                      outline: "none !important",
                      boxShadow: "none !important",
                      border: "none !important",
                    }}
                  >
                    <PopoverArrow />
                    <PopoverBody p={4}>
                      <Flex align="center" mb={2} gap={3}>
                        <Avatar
                          src={userProfile?.img_profile}
                          name={userProfile?.nickname || user.email}
                          bg="blue.400"
                          color="white"
                          size="md"
                          className="no-outline"
                          _focus={{
                            outline: "none",
                            boxShadow: "none",
                            border: "none",
                          }}
                          _active={{
                            outline: "none",
                            boxShadow: "none",
                            border: "none",
                          }}
                        />
                        <Box>
                          <Text fontWeight="bold" fontSize="lg">
                            {userProfile?.nickname || user.email.split("@")[0]}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {user.email}
                          </Text>
                        </Box>
                      </Flex>
                      <Divider my={2} />
                      {/* 어드민 페이지 버튼 */}
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          leftIcon={<SettingsIcon color="red.500" />}
                          w="100%"
                          justifyContent="flex-start"
                          color="red.600"
                          fontWeight="bold"
                          fontSize="md"
                          mb={1}
                          onClick={() => navigate("/admin")}
                          className="no-outline"
                          _focus={{
                            outline: "none",
                            boxShadow: "none",
                            border: "none",
                          }}
                          _active={{
                            outline: "none",
                            boxShadow: "none",
                            border: "none",
                          }}
                        >
                          어드민 페이지
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        leftIcon={
                          <img
                            src="/images/laeebly.png"
                            alt="레이블리"
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                            }}
                          />
                        }
                        w="100%"
                        justifyContent="flex-start"
                        color="teal.600"
                        fontWeight="normal"
                        fontSize="md"
                        mb={1}
                        ml={-1}
                        onClick={() => navigate("/laeeblyforshorts")}
                        className="no-outline"
                        _focus={{
                          outline: "none",
                          boxShadow: "none",
                          border: "none",
                        }}
                        _active={{
                          outline: "none",
                          boxShadow: "none",
                          border: "none",
                        }}
                      >
                        레이블리 for 쇼츠
                      </Button>
                      <Button
                        variant="ghost"
                        leftIcon={<SettingsIcon color="gray.500" />}
                        w="100%"
                        justifyContent="flex-start"
                        fontWeight="normal"
                        color="gray.700"
                        fontSize="md"
                        mb={1}
                        onClick={() => navigate("/mypage")}
                        className="no-outline"
                        _focus={{
                          outline: "none",
                          boxShadow: "none",
                          border: "none",
                        }}
                        _active={{
                          outline: "none",
                          boxShadow: "none",
                          border: "none",
                        }}
                      >
                        설정
                      </Button>
                      <Button
                        variant="ghost"
                        leftIcon={<Icon as={FiLogOut} color="gray.500" />}
                        w="100%"
                        justifyContent="flex-start"
                        fontWeight="normal"
                        color="gray.700"
                        fontSize="md"
                        onClick={handleLogout}
                        className="no-outline"
                        _focus={{
                          outline: "none",
                          boxShadow: "none",
                          border: "none",
                        }}
                        _active={{
                          outline: "none",
                          boxShadow: "none",
                          border: "none",
                        }}
                      >
                        로그아웃
                      </Button>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              ) : (
                <Button
                  colorScheme="blue"
                  onClick={() => navigate("/login")}
                  className="no-outline"
                  _focus={{
                    outline: "none",
                    boxShadow: "none",
                    border: "none",
                  }}
                  _active={{
                    outline: "none",
                    boxShadow: "none",
                    border: "none",
                  }}
                >
                  로그인
                </Button>
              )}
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* 협업 네비게이션 바 */}
      <Box
        bg={bgColor}
        borderBottom="1px"
        borderColor={borderColor}
        top="64px"
        zIndex={500}
      >
        <Container maxW="container.xl">
          <Flex h="60px" align="center" justify="space-between">
            <HStack
              spacing={8}
              align="center"
              overflowX="auto"
              w="100%"
              css={{
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                "-ms-overflow-style": "none",
                "scrollbar-width": "none",
              }}
            >
              {collaborationNavItems.map((item) => (
                <Link
                  key={item.path}
                  as={RouterLink}
                  to={item.path}
                  fontSize={{ base: "sm", md: "md" }}
                  fontWeight="medium"
                  color={location.pathname === item.path ? "blue.500" : "black"}
                  _hover={{ color: "blue.500", textDecoration: "none" }}
                  className="no-outline"
                  whiteSpace="nowrap"
                  _focus={{
                    outline: "none",
                    boxShadow: "none",
                    border: "none",
                  }}
                  _active={{
                    outline: "none",
                    boxShadow: "none",
                    border: "none",
                  }}
                >
                  {item.name}
                </Link>
              ))}
            </HStack>
            <HStack spacing={4} ml={4} flexShrink={0}>
              <Box display={{ base: "none", md: "none", lg: "block" }}>
                <Button
                  colorScheme="gray"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    window.open(
                      "https://support.google.com/youtube/answer/12504220?hl=ko#policies&zippy=%2C%EC%8B%A4%EC%A0%9C%EB%A1%9C%EC%9D%98%EB%AF%B8%ED%95%98%EB%82%98%EC%9A%94",
                      "_blank"
                    )
                  }
                >
                  <p style={{ fontSize: "13px", color: "gray" }}>
                    YouTube Shorts 수익 창출 정책 확인하기
                  </p>
                </Button>
              </Box>
              {/* {isContractData && (
                <Button
                  colorScheme="teal"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate("/collaboration/licensed-video-request")
                  }
                >
                  영상 라이센스 신청하기
                </Button>
              )} */}
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* 메인 컨텐츠 */}
      <Box as="main" flex={1} bg="gray.50">
        {children}
      </Box>

      {/* 하단바 */}
      <Box
        as="footer"
        bg={bgColor}
        borderTop="1px"
        borderColor={borderColor}
        py={4}
      >
        <Container maxW="container.xl">
          <Flex direction="column" spacing={2}>
            <HStack spacing={4} mb={8}>
              <ChakraLink href="/terms" fontWeight="bold">
                이용약관
              </ChakraLink>
              <ChakraLink href="/privacy" fontWeight="bold">
                개인정보처리방침
              </ChakraLink>
              <ChakraLink href="/digital-content-terms" fontWeight="bold">
                디지털 콘텐츠 유통약관
              </ChakraLink>
              <ChakraLink
                href="https://rhoonart.notion.site/e5c8239ce83c47578932782dd7211969"
                color="blue.500"
                target="_blank"
                rel="noopener noreferrer"
                fontWeight="bold"
              >
                음반 발매 메뉴얼
              </ChakraLink>
            </HStack>

            <Text fontSize="sm" color="gray.600" mb={2}>
              (주) 루나르트 | 대표 권재의
            </Text>
            <Text fontSize="sm" color="gray.600" mb={2}>
              주소: 서울특별시 성동구 연무장5가길 25, 8층 804호(성수동2가, 성수역 SK
              V1 Tower)
            </Text>
            <Text fontSize="sm" color="gray.600" mb={2}>
              사업자등록번호: 652-88-00793
            </Text>
            <Text fontSize="sm" color="gray.600" mb={2}>
              통신판매업 신고 2024-서울광진-0001
            </Text>
            <Text fontSize="sm" color="gray.600" mb={2}>
              고객 문의 contact@plpl.kr | 개인정보관리책임자 권재의
            </Text>
            <Text fontSize="sm" fontStyle="italic" color="gray.600" mb={2}>
              Believe in yourself © Rhoonart Co., Ltd. All Rights Reserved.
            </Text>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
};

export default CollaborationLayout;
